import {useOptionalCommentEditsContext} from '@github-ui/commenting/CommentEditsContext'
import {ConfirmationDialog} from '@primer/react'
import {useCallback} from 'react'

import {MESSAGES} from '../../constants/messages'
import {useDeleteIssueMutation} from '../../mutations/delete-issue-mutation'
import styles from './IssueDeletionConfirmationDialog.module.css'

type IssueDeletionConfirmationDialogProps = {
  issueId: string
  owner: string
  repository: string
  number: number
  onSuccessfulDeletion?: (issueId: string) => void
  afterDelete?: () => void
  onClose: () => void
}

export const IssueDeletionConfirmationDialog = ({
  issueId,
  owner,
  repository,
  number,
  onSuccessfulDeletion,
  afterDelete,
  onClose,
}: IssueDeletionConfirmationDialogProps) => {
  const commentEdits = useOptionalCommentEditsContext()
  const onCompleted = useCallback(() => {
    onSuccessfulDeletion?.(issueId)
    // Drafts on a deleted issue can no longer be saved, and leaving them active makes the
    // `beforeunload` guard prompt when `afterDelete` lands outside the app -- cancelling that
    // prompt strands the user on an issue that is already gone.
    commentEdits?.cancelAllCommentEdits()
    afterDelete?.()
  }, [afterDelete, commentEdits, issueId, onSuccessfulDeletion])
  const deleteIssueMutation = useDeleteIssueMutation({
    owner,
    repository,
    number,
    onCompleted,
  })

  const onDialogClose = useCallback(
    (gesture: 'confirm' | 'cancel' | 'close-button' | 'escape') => {
      if (gesture === 'confirm') {
        deleteIssueMutation.mutate()
      }
      onClose()
    },
    [deleteIssueMutation, onClose],
  )

  return (
    <ConfirmationDialog
      title={MESSAGES.deleteIssueConfirmationTitle}
      confirmButtonContent={MESSAGES.deleteIssueConfirmationButton}
      confirmButtonType="danger"
      onClose={onDialogClose}
    >
      <div className={styles.confirmationContent}>
        <ul className={styles.confirmationList}>
          {MESSAGES.deleteIssueConfirmationActionPoints.map(actionPoint => (
            <li key={actionPoint}>
              <span>{actionPoint}</span>
            </li>
          ))}
        </ul>
      </div>
    </ConfirmationDialog>
  )
}
